import React, { useState } from 'react'
import { Form, Radio, Spin, Table, Tabs } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { useRequest } from 'ahooks'
import dayjs from 'dayjs'
import Button from '@/components/button/button'
import SearchInput from '@/components/search-input/search-input'
import $msg from '@/utils/message'
import Auth from '@/views/main/components/auth/auth'
import { Modal as SgModal } from '@/components/dialog/modal'
import { layerManage, showLayer } from '@/utils/layer'
import { getBlacklistMembers, getBlacklistRecords, getShopInfo, unblacklistMember, updateMemberConfig } from '../api'
import MemberDetail from '../member-detail/member-detail'
import SettingPage, {
    SettingActions,
    SettingGrid,
    SettingPanel,
    SettingTableContainer,
} from '../member-setting/setting-page'
import { getMemberTablePagination, getTableTotal, MEMBER_TABLE_PAGE_SIZE } from '../table-utils'

const levelOptions = [
    { value: 1, label: '主动拉黑', desc: '顾客进入小程序时明确提示已被门店限制。' },
    { value: 2, label: '打烊关闭', desc: '顾客看到门店已打烊，不直接展示拉黑信息。' },
    { value: 3, label: '无法下单', desc: '顾客可以浏览商品，但无法提交订单。' },
]

const MemberBlacklist: React.FC = () => {
    const [form] = Form.useForm<{ miniapp_blacklist_level: number }>()
    const [tab, setTab] = useState('setting')
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(MEMBER_TABLE_PAGE_SIZE)
    const [keyword, setKeyword] = useState('')
    const [searchKey, setSearchKey] = useState(0)
    const shopReq = useRequest(getShopInfo, {
        onSuccess: res => {
            form.setFieldsValue({ miniapp_blacklist_level: res.membercard_config?.miniapp_blacklist_level || 1 })
        },
        onError: e => $msg('error', e.message || '获取拉黑设置失败'),
    })
    const isDirectBranch = shopReq.data?.shop_type === 1
    const memberReq = useRequest(() => getBlacklistMembers({ page, limit: pageSize, keyword: keyword || undefined }), {
        ready: tab === 'members',
        refreshDeps: [tab, page, pageSize, keyword],
        onError: e => $msg('error', e.message || '获取黑名单会员失败'),
    })
    const recordReq = useRequest(() => getBlacklistRecords({ page, limit: pageSize, keyword: keyword || undefined }), {
        ready: tab === 'records',
        refreshDeps: [tab, page, pageSize, keyword],
        onError: e => $msg('error', e.message || '获取拉黑记录失败'),
    })
    const saveReq = useRequest((values: { miniapp_blacklist_level: number }) => updateMemberConfig(values), {
        manual: true,
        onSuccess: () => {
            $msg('success', '保存成功')
            shopReq.refresh()
        },
        onError: e => $msg('error', e.message || '保存拉黑设置失败'),
    })
    const removeReq = useRequest(unblacklistMember, {
        manual: true,
        onSuccess: () => {
            $msg('success', '已解除拉黑')
            memberReq.refresh()
        },
        onError: e => $msg('error', e.message || '解除拉黑失败'),
    })
    const resetSearch = () => {
        setPage(1)
        setKeyword('')
        setSearchKey(value => value + 1)
    }
    const confirmUnblacklist = (record: any) => {
        showLayer(
            <SgModal
                isShow
                type="danger"
                headerStyles={{ width: '440px' }}
                style={{ height: '156px' }}
                okText="解除拉黑"
                onClose={() => layerManage.pop()}
                onCancel={() => layerManage.pop()}
                onConfirm={() => {
                    layerManage.pop()
                    removeReq.run({ member_id: record.id })
                }}
            >
                <div>
                    <div data-font-size-20 data-font-weight-600 data-color-000>
                        确认解除拉黑？
                    </div>
                    <div data-font-size-14 data-color-999 data-margin-top-8>
                        解除后，{record.name || record.user?.nickname || '该会员'} 将恢复小程序下单权限。
                    </div>
                </div>
            </SgModal>,
        )
    }
    const openMemberDetail = (memberId?: number) => {
        if (!memberId) return

        showLayer(<MemberDetail drawer memberId={memberId} onSuccess={() => memberReq.refresh()} />)
    }
    const memberColumns: ColumnsType<any> = [
        { title: '会员', width: 180, render: (_value, record) => record.name || record.user?.nickname || '--' },
        { title: '手机号', dataIndex: 'phone', width: 150, render: value => value || '--' },
        { title: '会员卡号', dataIndex: 'card_code', width: 160, render: value => value || '--' },
        { title: '会员等级', dataIndex: ['level', 'name'], width: 140, render: value => value || '--' },
        {
            title: '操作',
            width: 180,
            fixed: 'right',
            render: (_value, record) => (
                <>
                    <span className="action" onClick={() => openMemberDetail(record.id)}>
                        详情
                    </span>
                    <Auth perssion={20} onClick={() => !removeReq.loading && confirmUnblacklist(record)}>
                        <span className="action danger">{removeReq.loading ? '处理中' : '解除拉黑'}</span>
                    </Auth>
                </>
            ),
        },
    ]
    const recordColumns: ColumnsType<any> = [
        {
            title: '操作时间',
            dataIndex: 'create_time',
            width: 170,
            render: value => (value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '--'),
        },
        { title: '会员', dataIndex: ['member', 'name'], width: 170, render: value => value || '--' },
        { title: '手机号', dataIndex: ['member', 'phone'], width: 150, render: value => value || '--' },
        { title: '操作', dataIndex: 'operation_type_text', width: 120, render: value => value || '--' },
        { title: '限制方式', dataIndex: 'miniapp_blacklist_level_text', width: 150, render: value => value || '--' },
        { title: '操作人', dataIndex: 'operator_name', width: 140, render: value => value || '系统' },
        { title: '备注', dataIndex: 'remark', render: value => value || '--' },
    ]
    const activeReq = tab === 'members' ? memberReq : recordReq

    return (
        <SettingPage
            title="会员黑名单"
            extra={
                tab === 'setting' ? undefined : (
                    <>
                        <div className="member-basic-setting__search">
                            <SearchInput
                                key={searchKey}
                                placeholder="输入会员姓名或手机号"
                                onSearch={value => {
                                    setPage(1)
                                    setKeyword(value)
                                }}
                                onDelete={resetSearch}
                            />
                        </div>
                        <Button types="primary" onClick={resetSearch}>
                            重置
                        </Button>
                        <Button types="primary" loading={activeReq.loading} onClick={() => activeReq.refresh()}>
                            刷新
                        </Button>
                    </>
                )
            }
        >
            <Tabs
                activeKey={tab}
                onChange={value => {
                    setTab(value)
                    resetSearch()
                }}
            >
                <Tabs.TabPane tab="拉黑规则" key="setting" />
                <Tabs.TabPane tab="黑名单会员" key="members" />
                <Tabs.TabPane tab="操作记录" key="records" />
            </Tabs>
            {tab === 'setting' ? (
                <Spin spinning={shopReq.loading}>
                    <Form form={form} layout="vertical">
                        <SettingGrid>
                            <SettingPanel
                                title="顾客端限制方式"
                                desc="会员被拉黑后，甜杏小程序将按所选方式限制其下单。"
                                wide
                            >
                                {isDirectBranch ? (
                                    <div className="member-config-page__notice">
                                        该规则由总部统一设置，当前分店只能查看。
                                    </div>
                                ) : null}
                                <Form.Item name="miniapp_blacklist_level" label="拉黑后的展示方式">
                                    <Radio.Group disabled={isDirectBranch}>
                                        {levelOptions.map(option => (
                                            <Radio key={option.value} value={option.value}>
                                                <strong>{option.label}</strong>
                                                <span data-color-999 data-margin-left-8>
                                                    {option.desc}
                                                </span>
                                            </Radio>
                                        ))}
                                    </Radio.Group>
                                </Form.Item>
                                {!isDirectBranch ? (
                                    <SettingActions>
                                        <Auth
                                            perssion={20}
                                            onClick={() => form.validateFields().then(values => saveReq.run(values))}
                                        >
                                            <Button
                                                types="default"
                                                loading={saveReq.loading}
                                                disabled={saveReq.loading}
                                            >
                                                保存
                                            </Button>
                                        </Auth>
                                    </SettingActions>
                                ) : null}
                            </SettingPanel>
                        </SettingGrid>
                    </Form>
                </Spin>
            ) : (
                <SettingTableContainer className="member-basic-setting__panel">
                    <Table
                        bordered
                        rowKey={(record, index) => record.id || `${record.create_time}-${index}`}
                        loading={activeReq.loading}
                        columns={tab === 'members' ? memberColumns : recordColumns}
                        dataSource={
                            tab === 'members' ? memberReq.data?.member_list || [] : recordReq.data?.record_list || []
                        }
                        pagination={getMemberTablePagination({
                            current: page,
                            pageSize,
                            total: getTableTotal(activeReq.data?.pagination),
                            onChange: setPage,
                            onShowSizeChange: (nextPage, nextSize) => {
                                setPage(nextPage)
                                setPageSize(nextSize)
                            },
                        })}
                        scroll={{ x: 'max-content' }}
                    />
                </SettingTableContainer>
            )}
        </SettingPage>
    )
}

export default MemberBlacklist
